import {
  Card, CardHeader, CardBody, StatCard, Table, THead, TBody, TR, TH, TD,
  StatusBadge, EmptyState, ErrorNotice, Loading,
} from '../../components/ui/index.js';
import { useResource } from '../../hooks/useApi.js';
import { formatMoney, formatNumber, formatTimestamp } from '../../lib/format.js';
import { Activity } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

/**
 * AD-4 and AD-5 together: the two views that tell us something is broken before
 * a merchant calls. A provider outage shows up as one failure reason dominating
 * the last hour; a webhook problem shows up as a growing stuck count.
 */
export function AdminHealthPage() {
  const webhooks = useResource('/admin/webhooks/health');
  const failed = useResource('/admin/payments/failed', { params: { hours: 24, limit: 25 } });

  return (
    <>
      <PageHero chip="Platform" icon={Activity} title="Platform health" blurb="Webhook delivery and failed payments across all merchants." />

      {webhooks.error ? (
        <ErrorNotice error={webhooks.error} onRetry={webhooks.refetch} className="mb-6" />
      ) : (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Queue depth" loading={webhooks.loading} value={formatNumber(webhooks.data?.queueDepth)} hint="Waiting or retrying" />
          <StatCard label="Due now" loading={webhooks.loading} value={formatNumber(webhooks.data?.dueNow)} hint="Ready to send" />
          <StatCard label="Delivering" loading={webhooks.loading} value={formatNumber(webhooks.data?.delivering)} hint="Claimed by a worker" />
          <StatCard label="Gave up" loading={webhooks.loading} value={formatNumber(webhooks.data?.exhausted)}
            tone={webhooks.data?.exhausted > 0 ? 'danger' : 'default'} hint="Exhausted all 8 attempts" />
        </div>
      )}

      <div className="grid gap-6">
        <Card>
          <CardHeader title="Stuck webhook events" blurb="Failed or exhausted, most recent first" />
          {webhooks.loading && <Loading />}
          {!webhooks.loading && !webhooks.data?.stuck?.length && (
            <EmptyState title="Nothing stuck" blurb="Every webhook event has been delivered or is still within its retry schedule." />
          )}
          {webhooks.data?.stuck?.length > 0 && (
            <Table>
              <THead>
                <TR><TH>Event</TH><TH>Merchant</TH><TH>Status</TH><TH align="right">Attempts</TH><TH>Last error</TH><TH align="right">Next try</TH></TR>
              </THead>
              <TBody>
                {webhooks.data.stuck.map((event) => (
                  <TR key={event.id}>
                    <TD className="font-mono text-xs">{event.eventType}</TD>
                    <TD className="font-mono text-[11px] text-muted">{event.merchantId}</TD>
                    <TD><StatusBadge status={event.status} size="sm" /></TD>
                    <TD align="right" className="tabular text-xs">{event.attempts}</TD>
                    <TD className="max-w-xs truncate text-xs text-red-600">{event.lastError ?? '—'}</TD>
                    <TD align="right" className="text-xs text-muted">
                      {event.nextAttemptAt ? formatTimestamp(event.nextAttemptAt) : 'Gave up'}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>

        <Card>
          <CardHeader title="Failed payments" blurb="Across all merchants, last 24 hours" />
          {failed.loading && <Loading />}
          {failed.error && <ErrorNotice error={failed.error} onRetry={failed.refetch} className="m-4" />}

          {failed.data?.byReason?.length > 0 && (
            <CardBody className="border-b border-line">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">By reason</p>
              <div className="flex flex-wrap gap-2">
                {failed.data.byReason.map((reason) => (
                  <span key={reason.code} className="inline-flex items-center gap-1.5 rounded-full bg-surface-3 px-2.5 py-1 text-xs text-ink-soft">
                    <span className="font-mono">{reason.code}</span>
                    <span className="tabular font-semibold">{reason.count}</span>
                  </span>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted">
                One reason dominating this list is usually a provider problem, not a run of bad cards.
              </p>
            </CardBody>
          )}

          {!failed.loading && !failed.data?.payments?.length && (
            <EmptyState title="No failed payments" blurb="Nothing has failed across the platform in the last 24 hours." />
          )}

          {failed.data?.payments?.length > 0 && (
            <Table>
              <THead>
                <TR><TH>Payment</TH><TH>Merchant</TH><TH align="right">Amount</TH><TH>Reason</TH><TH align="right">When</TH></TR>
              </THead>
              <TBody>
                {failed.data.payments.map((payment) => (
                  <TR key={payment.id}>
                    <TD className="font-mono text-xs">{payment.id}</TD>
                    <TD className="font-mono text-[11px] text-muted">{payment.merchantId}</TD>
                    <TD align="right" className="tabular">{formatMoney(payment.amount, payment.currency)}</TD>
                    <TD className="text-xs text-ink-soft">{payment.failure?.code ?? '—'}</TD>
                    <TD align="right" className="text-xs text-muted">{formatTimestamp(payment.createdAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}

export default AdminHealthPage;
